import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pushApi,
  isPushSupported,
  getPushPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '../api/push';

export interface UsePushNotificationsReturn {
  // State
  isSupported: boolean;
  isEnabled: boolean;
  isLoading: boolean;
  permission: NotificationPermission;
  error: string | null;

  // Server config
  isConfigured: boolean;
  subscriptions: Array<{
    id: number;
    endpoint: string;
    device_name: string | null;
    created_at: string | null;
    last_used_at: string | null;
  }>;

  // Actions
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  sendTestNotification: () => Promise<boolean>;
  deleteSubscription: (id: number) => Promise<boolean>;
}

/**
 * Hook for managing push notifications
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  const queryClient = useQueryClient();
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [error, setError] = useState<string | null>(null);

  const isSupported = isPushSupported();

  // Fetch VAPID key
  const { data: vapidData, isLoading: isLoadingVapid } = useQuery({
    queryKey: ['push', 'vapid'],
    queryFn: pushApi.getVapidKey,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: isSupported,
  });

  // Fetch user's subscriptions
  const { data: subscriptions = [], isLoading: isLoadingSubscriptions } = useQuery({
    queryKey: ['push', 'subscriptions'],
    queryFn: pushApi.getSubscriptions,
    staleTime: 30 * 1000, // 30 seconds
    enabled: isSupported,
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: pushApi.subscribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscriptions'] });
    },
  });

  // Test notification mutation
  const testMutation = useMutation({
    mutationFn: pushApi.sendTest,
  });

  // Delete subscription mutation
  const deleteMutation = useMutation({
    mutationFn: pushApi.deleteSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscriptions'] });
    },
  });

  // Check current subscription status on mount
  useEffect(() => {
    if (!isSupported) return;

    setPermission(getPushPermission());

    getCurrentSubscription().then((sub) => {
      setIsEnabled(!!sub);
    });
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    setError(null);

    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    if (!vapidData?.is_configured || !vapidData?.public_key) {
      setError('Push notifications are not configured on the server');
      return false;
    }

    try {
      // Request permission if needed
      if (permission !== 'granted') {
        const newPermission = await requestNotificationPermission();
        setPermission(newPermission);

        if (newPermission !== 'granted') {
          setError('Notification permission denied');
          return false;
        }
      }

      // Subscribe to push
      const subscription = await subscribeToPush(vapidData.public_key);

      if (!subscription || !subscription.endpoint || !subscription.keys) {
        setError('Failed to create push subscription');
        return false;
      }

      // Send subscription to backend
      await subscribeMutation.mutateAsync({
        endpoint: subscription.endpoint,
        p256dh: subscription.keys.p256dh!,
        auth: subscription.keys.auth!,
        device_name: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop',
      });

      setIsEnabled(true);
      return true;
    } catch (err) {
      console.error('Failed to subscribe to push:', err);
      setError(err instanceof Error ? err.message : 'Failed to subscribe');
      return false;
    }
  }, [isSupported, vapidData, permission, subscribeMutation]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setError(null);

    try {
      const subscription = await getCurrentSubscription();

      if (subscription?.endpoint) {
        // Unsubscribe on backend
        await pushApi.unsubscribe(subscription.endpoint);
      }

      // Unsubscribe in browser
      await unsubscribeFromPush();

      setIsEnabled(false);
      queryClient.invalidateQueries({ queryKey: ['push', 'subscriptions'] });
      return true;
    } catch (err) {
      console.error('Failed to unsubscribe from push:', err);
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe');
      return false;
    }
  }, [queryClient]);

  // Send test notification
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    setError(null);

    try {
      const result = await testMutation.mutateAsync();
      if (result.sent === 0 && result.failed === 0) {
        setError('No active subscriptions. Please enable push notifications first.');
        return false;
      }
      return result.sent > 0;
    } catch (err) {
      console.error('Failed to send test notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to send test');
      return false;
    }
  }, [testMutation]);

  // Delete a subscription
  const deleteSubscription = useCallback(async (id: number): Promise<boolean> => {
    setError(null);

    try {
      await deleteMutation.mutateAsync(id);
      return true;
    } catch (err) {
      console.error('Failed to delete subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete');
      return false;
    }
  }, [deleteMutation]);

  return {
    isSupported,
    isEnabled,
    isLoading: isLoadingVapid || isLoadingSubscriptions || subscribeMutation.isPending,
    permission,
    error,
    isConfigured: vapidData?.is_configured ?? false,
    subscriptions,
    subscribe,
    unsubscribe,
    sendTestNotification,
    deleteSubscription,
  };
}

/**
 * Register the service worker
 * Note: Will fail silently with self-signed SSL certificates (dev environment)
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported');
    return null;
  }

  // Skip service worker if document is not ready
  if (document.readyState === 'loading') {
    console.info('Service Worker skipped: document not ready');
    return null;
  }

  // Skip in development mode
  if (import.meta.env.DEV) {
    console.info('Service Worker skipped: development mode');
    return null;
  }

  // Skip service worker in development with self-signed certs
  // Service workers require valid SSL or localhost without HTTPS
  if (window.location.hostname === 'localhost' && window.location.protocol === 'https:') {
    console.info('Service Worker skipped: self-signed SSL certificates are not supported. Push notifications disabled.');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('Service Worker registered:', registration.scope);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker available
            console.log('New service worker available');
          }
        });
      }
    });

    return registration;
  } catch (error) {
    // SSL certificate errors are expected in development
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('SSL') || errorMessage.includes('certificate')) {
      console.info('Service Worker skipped due to SSL certificate (expected in development)');
    } else {
      console.warn('Service Worker registration failed:', error);
    }
    return null;
  }
}
